import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Button } from 'tea-component';
import Loading from '@home/components/Loading';

import './index.scss'; // 样式文件

interface IImageUploaderProps extends IBaseProps {
  loading?: boolean;
  accept?: string;
  maxSize?: number; // 单位KB
  url?: string;
  enableDropUpload?: boolean;
  showPreviewUploadTrigger?: boolean;
  buttonLabel?: React.ReactNode;
  hideUploadButton?: boolean;
  onUpload: (file: File, url: string) => void;
  onClear?: () => void;
  onImageLoadError?: () => void;
}

export interface IImageUploaderRef {
  open: () => void;
}

const ImageUploader = forwardRef<IImageUploaderRef, IImageUploaderProps>((props, ref) => {
  const {
    style,
    className,
    loading,
    accept = 'image/*',
    maxSize = 100 * 1024,
    url,
    enableDropUpload = false,
    showPreviewUploadTrigger = false,
    buttonLabel,
    hideUploadButton = false,
    onUpload,
    onClear,
    onImageLoadError,
  } = props;

  const { formatMessage } = useIntl();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [imageLoading, setImageLoading] = useState(false);

  const [imageLoadError, setImageLoadError] = useState(false);

  const [previewUrl, setPreviewUrl] = useState('');

  const [dragging, setDragging] = useState(false);

  const showImgLoading = loading || imageLoading;

  const showImgPlaceholder = !previewUrl || imageLoadError;

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  useImperativeHandle(ref, () => ({ open: handleButtonClick }));

  const handleFile = (file?: File) => {
    if (!file) return;

    // 验证文件类型
    if (!file.type.match('image.*')) {
      return;
    }

    // 验证文件大小
    if (file.size > maxSize * 1024) {
      return;
    }

    // 生成预览图
    const reader = new FileReader();

    reader.onload = () => {
      const url = reader.result as string;
      fileInputRef.current!.value = '';
      setImageLoadError(false);
      setPreviewUrl(url);
      // 调用上传回调
      onUpload(file, url);
    };

    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enableDropUpload) return;
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageLoadError(true);
    onImageLoadError?.();
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageLoadError(false);
  };

  useEffect(() => {
    if (!!url) {
      setImageLoading(true);
    }
    setImageLoadError(false);
    setPreviewUrl(url || '');
  }, [url]);

  return (
    <div
      style={style}
      className={classnames('dbu-report-image-uploader-container', className, {
        loading: showImgLoading,
        dragging,
        'show-preview-upload-trigger': showPreviewUploadTrigger,
        'has-preview': !!previewUrl && !imageLoadError,
      })}
    >
      <div
        className="dbu-report-preview-container"
        onDragEnter={(e) => {
          if (!enableDropUpload) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragOver={(e) => {
          if (enableDropUpload) e.preventDefault();
        }}
        onDragLeave={() => enableDropUpload && setDragging(false)}
        onDrop={handleDrop}
      >
        {showImgLoading && <Loading />}
        {!showImgLoading && showImgPlaceholder && (
          <span className="dbu-report-fallback-text">
            {showPreviewUploadTrigger ? '+' : formatMessage({ id: 'dbu_common_upload_img_placeholder' })}
          </span>
        )}
        {!showImgPlaceholder && (
          <img className="dbu-report-preview-image" src={previewUrl} alt="" onError={handleImageError} onLoad={handleImageLoad} />
        )}
        {showPreviewUploadTrigger && showImgPlaceholder && !showImgLoading && (
          <button
            type="button"
            className="dbu-report-preview-upload-trigger"
            aria-label={formatMessage({ id: 'dbu_common_upload_img' })}
            onClick={handleButtonClick}
          />
        )}
        {!!previewUrl && !!onClear && !showImgLoading && (
          <button
            type="button"
            className="dbu-report-preview-clear"
            aria-label={formatMessage({ id: 'dbu_report_clear_avatar' })}
            onClick={onClear}
          />
        )}
      </div>

      <input style={{ display: 'none' }} ref={fileInputRef} type="file" accept={accept} onChange={handleFileChange} />

      {!hideUploadButton && (
        <Button htmlType="button" onClick={handleButtonClick}>
          {buttonLabel || formatMessage({ id: 'dbu_common_upload_img' })}
        </Button>
      )}
    </div>
  );
});

ImageUploader.displayName = 'ImageUploader';

export default ImageUploader;
